import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiRequest } from '../lib/utils';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const data = await apiRequest('/auth/me');
      setUser(data.user);
      setProfile(data.profile);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem('token');
      setUser(null);
      setProfile(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (phone, otp, role = 'patient') => {
    // Clear any existing session first
    localStorage.removeItem('token');
    setUser(null);
    setProfile(null);
    setIsAuthenticated(false);
    
    const data = await apiRequest('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ phone, otp, role }),
    });
    
    localStorage.setItem('token', data.token);
    setUser(data.user);
    setIsAuthenticated(true);
    
    // Fetch full profile
    await checkAuth();
    
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setProfile(null);
    setIsAuthenticated(false);
  };

  const updateProfile = async (updates) => {
    let endpoint = '/patient/profile';
    if (user?.role === 'doctor' || user?.role === 'nurse') {
      endpoint = '/provider/profile';
    } else if (user?.role === 'lab') {
      endpoint = '/lab/profile';
    } else if (user?.role === 'clinic') {
      endpoint = '/clinic/profile';
    }
    
    await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    
    await checkAuth();
  };

  const value = {
    user,
    profile,
    loading,
    isAuthenticated,
    login,
    logout,
    updateProfile,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
